import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, CheckCircle, Clock, Download, X } from '../icons';
import wechatQrUrl from '../../assets/wechat-official-account.jpg';
import {
  subscribeDownload, getDownloadState, closeDownloadDialog, openDownloadDialog,
  showUnconfirmedDownload, reportDownloadFailed, chooseDownloadLocation, retryDownload, resetDownload, type DownloadState,
} from '../../services/download';
import './download-dialog.css';

interface Props {
  state: DownloadState; onClose: () => void; onOpen: () => void; onHelp: () => void;
  onReportFailure: () => void; onRetry: () => void; onChooseLocation: () => void;
}

export function DownloadDialogView({ state, onClose, onOpen, onHelp, onReportFailure, onRetry, onChooseLocation }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const [seconds, setSeconds] = useState(10);
  const { scene, open } = state;
  const follow = scene === 'monitoring' || scene === 'success';
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    const previousOverflow = document.documentElement.style.overflow;
    const previousFocus = document.activeElement as HTMLElement | null;
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
    return () => {
      dialog.close(); document.documentElement.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
      else launcherRef.current?.focus({ preventScroll: true });
    };
  }, [open]);
  useEffect(() => {
    if (scene !== 'monitoring') return;
    const tick = () => setSeconds(Math.max(0, Math.ceil((state.startedAt + 10_000 - Date.now()) / 1000)));
    tick(); const timer = setInterval(tick, 200); return () => clearInterval(timer);
  }, [scene, state.startedAt]);

  const title = { monitoring: '正在确认下载', success: '文件已保存', failed: '下载未完成', unknown: '下载结果暂未确认' }[scene];
  let description: ReactNode;
  if (scene === 'unknown') description = <>下载或已成功，请在浏览器下载列表中确认文件已保存。</>;
  else if (scene === 'success') description = '文件已保存到您选择的位置，可前往查看。';
  else if (scene === 'failed') description = state.status?.status === 'completed'
    ? <>COS 已确认本次传输，重新获取链接不会重复扣次。</>
    : <>此次下载未完成。<strong>未确认成功前，不会扣除下载次数</strong>。</>;
  else if (state.phase === 'choose') description = '请选择文件保存位置，随后开始下载。';
  else if (state.phase === 'launch') description = '链接已准备好，点击开始下载后请查看浏览器下载列表。';
  else if (state.phase === 'transferring') description = `正在下载并保存${state.totalBytes ? `（${Math.floor(state.receivedBytes / state.totalBytes * 100)}%）` : ''}，请保持此页面打开。`;
  else if (state.phase === 'native') description = '下载已交给浏览器，请在下载列表中查看进度。';
  else description = '正在获取下载链接，请稍候。';

  return <>
    {!open && <aside className="dlp-launcher liquid-glass-panel rounded-sheet border border-line" aria-label="下载状态">
      <div><p className="text-xs font-medium text-fg">{title}</p><p className="mt-1 max-w-48 truncate text-xs text-fg-subtle">{state.name}</p></div>
      <button ref={launcherRef} className="dlp-button dlp-button-primary" type="button" onClick={onOpen}><Download aria-hidden="true" />查看下载</button>
    </aside>}
    <dialog ref={dialogRef} className={`dlp-dialog${follow ? ' dlp-dialog-with-wechat' : ''}`} aria-labelledby="dlp-title" aria-describedby="dlp-description"
      onCancel={event => { event.preventDefault(); onClose(); }}>
      <section className="dlp-card liquid-glass-panel rounded-sheet" data-state={scene}>
        <button type="button" className="dlp-icon-button" aria-label="关闭下载状态弹窗" onClick={onClose}><X aria-hidden="true" /></button>
        <div className="dlp-body">
          <div className="dlp-status-heading" aria-live="polite" aria-atomic="true">
            <div className="dlp-status-symbol rounded-card" aria-hidden="true">
              {scene === 'success' ? <CheckCircle /> : scene === 'failed' ? <AlertCircle /> : scene === 'unknown' ? <Clock /> :
                <svg className="dlp-checking-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M20 7v5h-5M4 17v-5h5M6.2 7a7 7 0 0 1 11.55-1.8L20 8M4 16l2.25 2.8A7 7 0 0 0 17.8 17" /></svg>}
            </div>
            <div className="dlp-title-group"><h2 id="dlp-title">{title}</h2><p className="dlp-dataset" title={state.name}>{state.name}</p></div>
          </div>
          <p id="dlp-description" className="dlp-description">{description}</p>
          {scene === 'failed' && state.advice && <p className="dlp-advice">{state.advice}</p>}
          <div className="dlp-actions">
            {scene === 'failed' ? state.canRetry
              ? <button type="button" className="dlp-button dlp-button-primary" onClick={onRetry}>重新获取下载链接</button>
              : <button type="button" className="dlp-button dlp-button-secondary" onClick={onClose}>关闭</button>
            : scene === 'success' || scene === 'unknown'
              ? <button type="button" className="dlp-button dlp-button-primary" onClick={onClose}>{scene === 'success' ? '完成' : '知道了'}</button>
            : <>
              <button type="button" className={`dlp-button ${state.phase === 'choose' || state.phase === 'launch' ? 'dlp-button-primary' : 'dlp-button-secondary'}`} onClick={state.phase === 'choose' || state.phase === 'launch' ? onChooseLocation : onClose}>{state.phase === 'choose' ? '选择保存位置' : state.phase === 'launch' ? '开始下载' : '收起窗口'}</button>
              <button type="button" className="dlp-text-button" disabled={seconds > 0} onClick={onHelp}>下载没有开始？{seconds > 0 && <span className="dlp-cooldown">（{seconds} 秒）</span>}</button>
            </>}
          </div>
          {scene === 'unknown' && <div className="dlp-feedback"><button type="button" className="dlp-text-button" onClick={onReportFailure}>我没有成功下载</button></div>}
        </div>
        {follow && <aside className="dlp-wechat" aria-labelledby="dlp-wechat-title">
          <div className="dlp-wechat-copy"><h3 id="dlp-wechat-title">微信扫码关注</h3><p className="dlp-wechat-name">知行经管科研网</p></div>
          <img className="dlp-wechat-qr" src={wechatQrUrl} alt="知行经管科研网微信公众号二维码" width={258} height={258} />
          <p className="dlp-wechat-tip">数据上新 · 免费福利</p>
        </aside>}
      </section>
    </dialog>
  </>;
}

export default function DownloadStatusDialog() {
  const state = useSyncExternalStore(subscribeDownload, getDownloadState);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const previousUser = useRef(user?.id);
  useEffect(() => {
    if (previousUser.current !== user?.id) resetDownload();
    previousUser.current = user?.id;
  }, [user?.id]);
  useEffect(() => {
    const refresh = () => {
      void queryClient.invalidateQueries({ queryKey: ['my-entitlements'] });
      void queryClient.invalidateQueries({ queryKey: ['entitlements'] });
      void queryClient.invalidateQueries({ queryKey: ['my-downloads'] });
    };
    window.addEventListener('zx:download-settled', refresh);
    return () => window.removeEventListener('zx:download-settled', refresh);
  }, [queryClient]);
  return state && <DownloadDialogView state={state} onClose={closeDownloadDialog} onOpen={openDownloadDialog} onHelp={showUnconfirmedDownload}
    onReportFailure={reportDownloadFailed} onRetry={() => { void retryDownload(); }} onChooseLocation={chooseDownloadLocation} />;
}
